"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export function RequestDemoButton({ size, className, asLink }: { size?: "lg" | "sm" | "default"; className?: string; asLink?: boolean }) {
  const [open, setOpen] = useState(false)
  const [businessName, setBusinessName] = useState("")
  const [telephone, setTelephone] = useState("")
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setStatus("idle")

    try {
      const res = await fetch("/api/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessName, telephone, email }),
      })

      if (res.ok) {
        setStatus("success")
        setBusinessName("")
        setTelephone("")
        setEmail("")
      } else {
        setStatus("error")
      }
    } catch {
      setStatus("error")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {asLink ? (
        <button
          onClick={() => { setStatus("idle"); setOpen(true) }}
          className="underline text-blue-700 dark:text-blue-400 cursor-pointer bg-transparent border-none p-0 text-sm"
        >
          request a demo
        </button>
      ) : (
        <Button size={size} variant="outline" className={`hover:bg-[var(--accent-web)] hover:text-white hover:border-[var(--accent-web)] transition-colors ${className ?? ""}`} onClick={() => { setStatus("idle"); setOpen(true) }}>
          Request a Demo
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request a Demo</DialogTitle>
            <DialogDescription>
              Fill in your details and our team will get back to you shortly.
            </DialogDescription>
          </DialogHeader>

          {status === "success" ? (
            <div className="py-6 text-center space-y-2">
              <div className="text-4xl">✅</div>
              <p className="text-lg font-semibold">Request Sent!</p>
              <p className="text-gray-500 text-sm">We'll be in touch with you soon.</p>
              <Button className="mt-4" onClick={() => setOpen(false)}>Close</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
              <div className="space-y-1">
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  placeholder="e.g. Acme Pvt Ltd"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="telephone">Telephone</Label>
                <Input
                  id="telephone"
                  type="tel"
                  placeholder="e.g. +94 71 234 5678"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {status === "error" && (
                <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="ghost" className="hover:bg-[var(--accent-web)] hover:text-white" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-blue-800 to-green-700 hover:from-blue-900 hover:to-green-800"
                >
                  {submitting ? "Sending..." : "Submit Request"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
